import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import PageTitle from '../common/PageTitle'
import useGenericEndpoint from '../hooks/api/useGenericEndpoint'
import { blue, grey } from '@mui/material/colors'
import { ExpandMore } from '@mui/icons-material'
import useIsAdmin from '../hooks/useIsAdmin'
import useIsMobile, { useScreenSize } from '../hooks/useIsMobile'
import TabsOrMobileDropdown from '../common/TabsOrMobileDropdown'

const domainRegex =
  /(https?:\/\/(.+?\.)?youtube\.com(\/[A-Za-z0-9\-\._~:\/\?#\[\]@!$&'\(\)\*\+,;\=]*)?)/

const SupportView = () => {
  const [tab, setTab] = useState(0)

  const [sections, setSections] = useState([])

  const { data, isLoading } = useGenericEndpoint(
    '/api/superadmin/support-sections'
  )

  useEffect(() => {
    if (isLoading) return
    setSections(data?.sections || [])
  }, [data, isLoading])

  const selectedSection = sections[tab]

  const { isPriveledged } = useIsAdmin()

  const { windowWidth } = useScreenSize()

  const isMobile = windowWidth < 1200

  if (isLoading) return <CircularProgress />

  const displaySections = isPriveledged
    ? sections
    : sections.filter((s) => s.generalUsersCanSee)

  const frameWidth = Math.min(windowWidth * 0.7, 560)
  const frameHeight = frameWidth * 0.6

  return (
    <Box>
      <PageTitle>Support Libraries</PageTitle>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Box sx={{ mr: 2, width: '225px' }}>
            <Typography sx={{ mt: 2 }}>Sections</Typography>
            {displaySections.length > 0 && (
              <>
                {isMobile && (
                  <TabsOrMobileDropdown
                    override={isMobile}
                    tabs={displaySections.map((s) => ({ label: s.name }))}
                    tab={tab}
                    setTab={setTab}
                  />
                )}
                {!isMobile && (
                  <Tabs
                    orientation='vertical'
                    sx={{
                      mt: 1,
                      flexShrink: 0,
                      maxHeight: '400px',
                      backgroundColor: 'white',
                      border: `1px solid ${grey[300]}`,

                      flexShrink: 0,
                    }}
                    value={tab}
                    onChange={(_e, val) => setTab(val)}
                    variant='scrollable'
                  >
                    {displaySections.map((s, i) => {
                      return (
                        <Tab
                          sx={{
                            alignItems: 'start',
                            borderTop: `1px solid ${grey[200]}`,
                            backgroundColor: tab === i ? blue[50] : null,
                          }}
                          key={i}
                          label={s.name}
                        ></Tab>
                      )
                    })}
                  </Tabs>
                )}
              </>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, p: 2 }}>
            {selectedSection && (
              <Box>
                <Typography sx={{ textAlign: 'center', mb: 2 }} variant='h5'>
                  {selectedSection.name}
                </Typography>

                {selectedSection.subsections.map((ss, i) => {
                  const linkIsValid =
                    ss.videoLink && domainRegex.test(ss.videoLink)

                  return (
                    <Accordion elevation={8} defaultExpanded={i === 0} key={i}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        {ss.name}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            mt: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          {linkIsValid && (
                            <>
                              <iframe
                                sandbox='allow-scripts allow-same-origin'
                                width={frameWidth}
                                height={frameHeight}
                                src={ss.videoLink}
                                title='YouTube video player'
                                frameBorder='0'
                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen'
                                allowFullScreen
                              ></iframe>

                              <Typography
                                sx={{ mt: 2, mb: ss.pictureLink ? 4 : 0 }}
                              >
                                {ss.videoDescription}
                              </Typography>
                            </>
                          )}
                          {ss.pictureLink && (
                            <>
                              <img
                                style={{
                                  width: `${frameWidth}px`,
                                }}
                                src={ss.pictureLink}
                              ></img>

                              <Typography sx={{ mt: 2 }}>
                                {ss.pictureDescription}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SupportView
